import { useState, useEffect, useMemo } from "react";
import type { SelectChangeEvent } from "@mui/material/Select";
import DSCard from "views/system-design/DSCard";
import DSCardHeader from "views/system-design/DSCardHeader";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import firebaseService from "firebase_service/firebaseService";
import { Version } from "Types";

function NewClockOSFeaturesCard() {
  const [versions, setVersions] = useState<Version[]>();
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [fromVersion, setFromVersion] = useState<number>();
  const [toVersion, setToVersion] = useState<number>();

  useEffect(() => {
    const listener = (allVersions: Version[]) => {
      setVersions([...allVersions].sort((a, b) => b.version - a.version));
    };

    firebaseService.listen<Version>("version", listener);
    return () => {
      firebaseService.removeListener("version", listener);
    };
  }, []);

  const publicVersion = firebaseService.general?.public;
  const from = fromVersion ?? publicVersion;
  const to = toVersion ?? versions?.[0]?.version;

  const displayedVersions = useMemo(
    () =>
      versions?.filter(
        (v) =>
          from != null &&
          to != null &&
          v.version > from &&
          v.version <= to &&
          (!featuredOnly || v.isFeature),
      ),
    [versions, from, to, featuredOnly],
  );

  const handleFromChange = (value: number) => {
    setFromVersion(value);
    if (toVersion != null && toVersion <= value) {
      setToVersion(undefined);
    }
  };

  const latestVersion = versions?.[0]?.version;

  const versionLabel = (v: Version) =>
    `${v.version}${v.version === latestVersion ? " (latest)" : ""}${
      v.version === publicVersion ? " (current)" : ""
    }${v.deprecated ? " (deprecated)" : ""}`;

  return (
    <DSCard
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
      }}
    >
      <DSCardHeader
        title={
          <DSTypography variant="h6" sx={{ textTransform: "none" }}>
            New ClockOS Features
          </DSTypography>
        }
        action={
          <DSStack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ whiteSpace: "nowrap" }}
          >
            <DSTypography variant="caption" color="text.secondary">
              From
            </DSTypography>
            <DSSelect<number>
              size="small"
              sx={{ typography: "caption" }}
              value={from ?? ""}
              onChange={(e: SelectChangeEvent<number>) =>
                handleFromChange(Number(e.target.value))
              }
              aria-label="From version"
            >
              {versions?.map((v) => (
                <DSMenuItem key={v.id} value={v.version}>
                  {versionLabel(v)}
                </DSMenuItem>
              ))}
            </DSSelect>
            <DSTypography variant="caption" color="text.secondary">
              To
            </DSTypography>
            <DSSelect<number>
              size="small"
              sx={{ typography: "caption" }}
              value={to ?? ""}
              onChange={(e: SelectChangeEvent<number>) =>
                setToVersion(Number(e.target.value))
              }
              aria-label="To version"
            >
              {versions
                ?.filter((v) => from == null || v.version > from)
                .map((v) => (
                  <DSMenuItem key={v.id} value={v.version}>
                    {versionLabel(v)}
                  </DSMenuItem>
                ))}
            </DSSelect>
            <DSFormControlLabel
              sx={{ margin: 0, whiteSpace: "nowrap" }}
              control={
                <DSCheckbox
                  size="small"
                  checked={featuredOnly}
                  onChange={(e) => setFeaturedOnly(e.target.checked)}
                />
              }
              label={
                <DSTypography variant="caption">Features only</DSTypography>
              }
            />
          </DSStack>
        }
      />
      <DSCardContent sx={{ flex: 1 }}>
        {!versions ? (
          <DSBox sx={{ textAlign: "center", padding: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : !displayedVersions?.length ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", padding: "20px" }}
          >
            No versions in range
          </DSTypography>
        ) : (
          <DSBox sx={{ maxHeight: 300, overflowY: "auto" }}>
            {displayedVersions.map((v) => (
              <DSTypography
                key={v.id}
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 1, whiteSpace: "pre-line" }}
              >
                {v.description}
              </DSTypography>
            ))}
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default NewClockOSFeaturesCard;
